/**
 * CreateObjectTypeWizard - Create a new custom object type.
 *
 * Multi-step wizard for creating custom object types:
 * 1. Basics — display/plural name + icon
 * 2. Type — which ontology kind this object is (required)
 * 3. Fields — custom properties
 * 4. Associations — relationships with other object types (+ notes toggle)
 * 5. Stages — the pipeline, for the one kind that rides one
 * 6. Review — summary + create
 *
 * The step list is conditional: Stages exists only when the chosen kind rides
 * a pipeline, so a step's position is not stable and anything pointing at a
 * step names it by id (`./shared/objectTypeWizardSteps`).
 *
 * Persisted via the flat events endpoint. UI chrome (modal shell, step
 * indicator, footer, dirty-close confirmation) is provided by the base
 * `Wizard` component; this file is responsible for the form state, the
 * step bodies, and the submit logic.
 */

import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core';
import {
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';

import { useUser } from '@/allauth_auth/hooks.js';
import { useOrg } from '@/contexts/OrgContext';
import { FormProvider, useFormContext } from '@/lib/base/forms/form-context';
import { useWizardContext, Wizard, type WizardStep } from '@/lib/base/wizard';
import { getUserFriendlyEventError } from '@/lib/crm/core/errors';
import { createEventsClient } from '@/lib/crm/core/EventsClient';
import type { OntologyKind } from '@/lib/crm/core/ontologyKinds';
import {
  getOntologyKindLabel,
  kindRidesPipeline,
} from '@/lib/crm/core/ontologyKinds';
import { createSchemaClient } from '@/lib/crm/core/SchemaClient';
import { invalidateSchemaCaches } from '@/lib/crm/query';

import { type AssociationDefinition } from './InlineAssociationEditor';
import { type PropertyDefinition } from './InlinePropertyEditor';
import { buildObjectTypeEvents } from './shared/buildObjectTypeEvents';
import {
  OBJECT_TYPE_WIZARD_STEPS,
  type ObjectTypeWizardStepId,
} from './shared/objectTypeWizardSteps';
import { ReviewStep } from './shared/ReviewComponents';
import { createDefaultStages, type StageItem } from './shared/StageManager';
import { AssociationsStepBody } from './shared/wizardSteps/AssociationsStepBody';
import { BasicsStepBody } from './shared/wizardSteps/BasicsStepBody';
import { InlinePropertyEditorStepBody } from './shared/wizardSteps/InlinePropertyEditorStepBody';
import { OntologyKindStepBody } from './shared/wizardSteps/OntologyKindStepBody';
import { PipelineStepBody } from './shared/wizardSteps/PipelineStepBody';
import {
  validateDisplayName,
  validateIcon,
  validateOntologyKind,
  validatePipelineName,
  validatePluralName,
} from './shared/wizardValidation';

// =============================================================================
// Constants
// =============================================================================

/** Step IDs for `WizardStep.id` and any cross-step references. */
const {
  basics: STEP_BASICS,
  kind: STEP_KIND,
  fields: STEP_FIELDS,
  associations: STEP_ASSOCIATIONS,
  pipeline: STEP_PIPELINE,
  review: STEP_REVIEW,
} = OBJECT_TYPE_WIZARD_STEPS;

/**
 * FormProvider step numbers — used to scope `isStepValid` / `touchStep`
 * calls. Kept as constants so the mapping between Wizard steps and
 * FormProvider step indices is explicit.
 */
const FORM_STEP_BASICS = 1;
const FORM_STEP_KIND = 2;
const FORM_STEP_PIPELINE = 4;

const FIELD_LABELS = {
  displayName: 'Display name',
  pluralName: 'Plural name',
  icon: 'Icon',
  pipelineName: 'Pipeline name',
} as const;

// Hoisted so useSensor's internal memoization (keyed on this options object's
// identity) actually holds across renders — an inline object literal here
// would defeat it and make `sensors` a new array every render, which then
// falsely invalidated the `steps` useMemo below.
const POINTER_SENSOR_OPTIONS = { activationConstraint: { distance: 8 } };
const KEYBOARD_SENSOR_OPTIONS = {
  coordinateGetter: sortableKeyboardCoordinates,
};

// =============================================================================
// Public props
// =============================================================================

export interface CreateObjectTypeWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (entityTypeName: string) => void;
  orgId: number;
}

interface ReviewStepProps {
  displayName: string;
  pluralName: string;
  icon: string | null;
  ontologyKindLabel: string;
  properties: PropertyDefinition[];
  associations: AssociationDefinition[];
  notesEnabled: boolean;
  isPipelineable: boolean;
  stages: StageItem[];
  error: string | null;
  onJumpToStep: (stepId: ObjectTypeWizardStepId) => void;
}

function ReviewStepBody({
  displayName,
  pluralName,
  icon,
  ontologyKindLabel,
  properties,
  associations,
  notesEnabled,
  isPipelineable,
  stages,
  error,
  onJumpToStep,
}: ReviewStepProps) {
  return (
    <div className='space-y-4'>
      {error && (
        <div
          role='alert'
          className='text-sm bg-utility-error-50 border border-utility-error-200 rounded-lg p-3 text-[var(--color-text-error-primary)]'
        >
          {error}
        </div>
      )}
      <ReviewStep
        displayName={displayName}
        pluralName={pluralName}
        icon={icon}
        ontologyKindLabel={ontologyKindLabel}
        properties={properties}
        associations={associations}
        notesEnabled={notesEnabled}
        isPipelineable={isPipelineable}
        stages={stages}
        onStepClick={onJumpToStep}
      />
    </div>
  );
}

// =============================================================================
// Inner wizard — uses useFormContext to wire validation gating
// =============================================================================

interface InnerProps extends CreateObjectTypeWizardProps {
  // State + setters (owned by the outer component)
  displayName: string;
  pluralName: string;
  icon: string | null;
  ontologyKind: OntologyKind | null;
  isPipelineable: boolean;
  pipelineName: string;
  stages: StageItem[];
  properties: PropertyDefinition[];
  associations: AssociationDefinition[];
  notesEnabled: boolean;
  error: string | null;
  activeStage: StageItem | null;
  validationErrors: Record<string, { name?: string }>;
  hasValidationErrors: boolean;
  generatedTypeName: string;
  sensors: ReturnType<typeof useSensors>;
  isDirty: boolean;

  onDisplayNameChange: (value: string) => void;
  onDisplayNameBlur: () => void;
  onPluralNameChange: (value: string) => void;
  onIconChange: (value: string | null) => void;
  onOntologyKindChange: (value: OntologyKind) => void;
  onPipelineNameChange: (value: string) => void;
  onStageNameChange: (id: string, name: string) => void;
  onStageDelete: (id: string) => void;
  onStageAdd: () => void;
  onDragStart: (event: DragStartEvent) => void;
  onDragEnd: (event: DragEndEvent) => void;
  onDragCancel: () => void;
  onPropertiesChange: (props: PropertyDefinition[]) => void;
  onAssociationsChange: (assocs: AssociationDefinition[]) => void;
  onNotesEnabledChange: (enabled: boolean) => void;

  setError: (error: string | null) => void;
  doSubmit: () => Promise<void>;
}

function CreateObjectTypeWizardInner({
  isOpen,
  onClose,
  orgId,
  // State
  displayName,
  pluralName,
  icon,
  ontologyKind,
  isPipelineable,
  pipelineName,
  stages,
  properties,
  associations,
  notesEnabled,
  error,
  activeStage,
  validationErrors,
  hasValidationErrors,
  generatedTypeName,
  sensors,
  isDirty,
  // Handlers
  onDisplayNameChange,
  onDisplayNameBlur,
  onPluralNameChange,
  onIconChange,
  onOntologyKindChange,
  onPipelineNameChange,
  onStageNameChange,
  onStageDelete,
  onStageAdd,
  onDragStart,
  onDragEnd,
  onDragCancel,
  onPropertiesChange,
  onAssociationsChange,
  onNotesEnabledChange,
  setError,
  doSubmit,
}: InnerProps) {
  const { isStepValid, touchStep, stepErrorSummary, scrollToField } =
    useFormContext();

  // Build steps array. Identity changes only when state or handlers change.
  // The Review step's "jump to a previous step" UX is wired via
  // ReviewStepWithJump, which uses useWizardContext to call goTo — no
  // closure plumbing needed here.
  const steps = useMemo<WizardStep[]>(() => {
    const kindStep: WizardStep = {
      id: STEP_KIND,
      label: 'Type',
      title: 'What kind of thing is this?',
      subtitle:
        'Ahoy uses this to link your records to the right things, act on them in AI Actions, and include them in reports.',
      render: () => (
        <OntologyKindStepBody
          ontologyKind={ontologyKind}
          onOntologyKindChange={onOntologyKindChange}
        />
      ),
      canAdvance: () => isStepValid(FORM_STEP_KIND),
      onBeforeNext: () => {
        touchStep(FORM_STEP_KIND);
        if (!isStepValid(FORM_STEP_KIND)) {
          throw new Error('Validation failed for Type step');
        }
      },
    };

    // The Pipeline step exists only for the kind that rides stages. For every
    // other kind the question is meaningless, so the wizard is one step
    // shorter rather than showing a toggle that must stay off.
    const pipelineSteps: WizardStep[] = kindRidesPipeline(ontologyKind)
      ? [
          {
            id: STEP_PIPELINE,
            label: 'Stages',
            title: 'Set up your stages',
            subtitle: 'Records move through these stages, in this order.',
            render: () => (
              <PipelineStepBody
                pipelineName={pipelineName}
                onPipelineNameChange={onPipelineNameChange}
                stages={stages}
                activeStage={activeStage}
                validationErrors={validationErrors}
                sensors={sensors}
                onStageNameChange={onStageNameChange}
                onStageDelete={onStageDelete}
                onStageAdd={onStageAdd}
                onDragStart={onDragStart}
                onDragEnd={onDragEnd}
                onDragCancel={onDragCancel}
              />
            ),
            canAdvance: () => isStepValid(FORM_STEP_PIPELINE),
            onBeforeNext: () => {
              touchStep(FORM_STEP_PIPELINE);
              if (!isStepValid(FORM_STEP_PIPELINE)) {
                const errs = stepErrorSummary(FORM_STEP_PIPELINE);
                if (errs.length > 0) {
                  scrollToField(errs[0].name);
                }
                throw new Error('Validation failed for Pipeline step');
              }
            },
          },
        ]
      : [];

    return [
      {
        id: STEP_BASICS,
        label: 'Basics',
        title: 'What are you tracking?',
        subtitle: 'Give your new object type a name and icon.',
        render: () => (
          <BasicsStepBody
            displayName={displayName}
            pluralName={pluralName}
            icon={icon}
            onDisplayNameChange={onDisplayNameChange}
            onDisplayNameBlur={onDisplayNameBlur}
            onPluralNameChange={onPluralNameChange}
            onIconChange={onIconChange}
          />
        ),
        canAdvance: () => isStepValid(FORM_STEP_BASICS),
        onBeforeNext: () => {
          touchStep(FORM_STEP_BASICS);
          if (!isStepValid(FORM_STEP_BASICS)) {
            const errs = stepErrorSummary(FORM_STEP_BASICS);
            if (errs.length > 0) {
              scrollToField(errs[0].name);
            }
            throw new Error('Validation failed for Basics step');
          }
        },
      },
      kindStep,
      {
        id: STEP_FIELDS,
        label: 'Fields',
        title: 'Define your fields',
        subtitle: 'Add the properties you want to track for each record.',
        render: () => (
          <InlinePropertyEditorStepBody
            properties={properties}
            onPropertiesChange={onPropertiesChange}
          />
        ),
      },
      {
        id: STEP_ASSOCIATIONS,
        label: 'Associations',
        title: 'Associations',
        subtitle: 'Set up relationships between this object type and others.',
        render: () => (
          <AssociationsStepBody
            pluralName={pluralName}
            notesEnabled={notesEnabled}
            onNotesEnabledChange={onNotesEnabledChange}
            associations={associations}
            onAssociationsChange={onAssociationsChange}
            generatedTypeName={generatedTypeName}
            orgId={orgId}
          />
        ),
      },
      ...pipelineSteps,
      {
        id: STEP_REVIEW,
        label: 'Review',
        title: 'Review',
        subtitle: ' ', // non-empty char so subtitle slot still reserves space; intentional
        render: () => (
          <ReviewStepWithJump
            displayName={displayName}
            pluralName={pluralName}
            icon={icon}
            ontologyKindLabel={getOntologyKindLabel(ontologyKind)}
            properties={properties}
            associations={associations}
            notesEnabled={notesEnabled}
            isPipelineable={isPipelineable}
            stages={stages}
            error={error}
          />
        ),
        primaryCta: {
          label: 'Create object type',
        },
      },
    ];
  }, [
    displayName,
    pluralName,
    icon,
    ontologyKind,
    isPipelineable,
    pipelineName,
    stages,
    properties,
    associations,
    notesEnabled,
    activeStage,
    validationErrors,
    sensors,
    generatedTypeName,
    error,
    orgId,
    isStepValid,
    touchStep,
    stepErrorSummary,
    scrollToField,
    onDisplayNameChange,
    onDisplayNameBlur,
    onPluralNameChange,
    onIconChange,
    onOntologyKindChange,
    onPipelineNameChange,
    onStageNameChange,
    onStageDelete,
    onStageAdd,
    onDragStart,
    onDragEnd,
    onDragCancel,
    onPropertiesChange,
    onAssociationsChange,
    onNotesEnabledChange,
  ]);

  // Combined validation + submit: runs on the Review step's primary CTA.
  // The wizard handles isSubmitting state internally for the duration of
  // the returned promise; throw to keep the user on the current step.
  const handleConfirm = useCallback(async () => {
    if (isPipelineable && stages.length === 0) {
      setError('At least one stage is required for objects with a pipeline');
      throw new Error('No stages');
    }
    if (isPipelineable && hasValidationErrors) {
      setError('Please fix validation errors in stages');
      throw new Error('Stage validation');
    }
    if (properties.length > 0) {
      const labelSet = new Set<string>();
      for (const prop of properties) {
        if (!prop.label.trim()) {
          setError('All properties must have a label');
          throw new Error('Property validation');
        }
        const normalizedLabel = prop.label.toLowerCase().trim();
        if (labelSet.has(normalizedLabel)) {
          setError(`Duplicate property label "${prop.label}"`);
          throw new Error('Duplicate property');
        }
        labelSet.add(normalizedLabel);
        if (prop.fieldType === 'select' && prop.options.length < 2) {
          setError(`Property "${prop.label}" requires at least 2 options`);
          throw new Error('Empty select options');
        }
      }
    }
    if (associations.length > 0) {
      const targetSet = new Set<string>();
      for (const assoc of associations) {
        if (!assoc.targetEntityTypeName) {
          setError('All associations must have a target object type');
          throw new Error('Association target');
        }
        if (targetSet.has(assoc.targetEntityTypeName)) {
          setError(
            `Duplicate association target "${assoc.targetEntityTypeName}"`
          );
          throw new Error('Duplicate association');
        }
        targetSet.add(assoc.targetEntityTypeName);
        if (!assoc.displayName.trim()) {
          setError('All associations must have a display name');
          throw new Error('Association display name');
        }
        if (!assoc.displayNameReverse.trim()) {
          setError('All associations must have a reverse display name');
          throw new Error('Association reverse display name');
        }
      }
    }
    await doSubmit();
  }, [
    isPipelineable,
    stages.length,
    hasValidationErrors,
    properties,
    associations,
    setError,
    doSubmit,
  ]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        onClose();
      }
    },
    [onClose]
  );

  return (
    <Wizard
      mode='fullscreen'
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      steps={steps}
      onConfirm={handleConfirm}
      isDirty={isDirty}
      dialogAriaLabel='Create object type'
      instance='create-object-type'
    />
  );
}

// Wrapper that wires the review pane's jump targets to the wizard's `goTo`.
// Takes every ReviewStepBody prop except `onJumpToStep`, which it sources from
// useWizardContext. `goTo` accepts a step id directly, so the id travels from
// the Edit button to the wizard untranslated — nothing here has to know where
// a step currently sits in the list.
type ReviewStepWithJumpProps = Omit<ReviewStepProps, 'onJumpToStep'>;

function ReviewStepWithJump(props: ReviewStepWithJumpProps) {
  const { goTo } = useWizardContext();
  return <ReviewStepBody {...props} onJumpToStep={goTo} />;
}

// =============================================================================
// Outer component — owns state, FormProvider, and submit machinery
// =============================================================================

export function CreateObjectTypeWizard({
  isOpen,
  onClose,
  onCreated,
  orgId,
}: CreateObjectTypeWizardProps) {
  const queryClient = useQueryClient();
  const user = useUser();
  // Refresh OrgContext after creation so currentOrg.permissions reflects
  // the newly-created entity types. Backend dynamically expands
  // ahoy_default and ahoy_schema_admin with permissions for all custom
  // (org_*) entity types when permissions are resolved per request — but
  // the browser's cached OrgContext was populated before the new types
  // existed, so until we re-fetch /orgs/api/orgs/, hasPermission(...) for
  // the new type returns false and permission-gated UI is hidden.
  const { refreshCurrentOrg } = useOrg();

  // -------------------------------------------------------------------------
  // Form state
  // -------------------------------------------------------------------------

  const [displayName, setDisplayName] = useState('');
  const [pluralName, setPluralName] = useState('');
  const [icon, setIcon] = useState<string | null>(null);
  const [ontologyKind, setOntologyKind] = useState<OntologyKind | null>(null);
  // Derived from `ontologyKind` — a pipeline is the shape of exactly one kind,
  // so this is never toggled independently. Kept as state (not a plain
  // derivation) because picking the pipeline kind also seeds default stages
  // and a pipeline name, which are edits, not derivations.
  const [isPipelineable, setIsPipelineable] = useState(false);
  const [pipelineName, setPipelineName] = useState('');
  const [stages, setStages] = useState<StageItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [properties, setProperties] = useState<PropertyDefinition[]>([]);
  const [associations, setAssociations] = useState<AssociationDefinition[]>([]);
  const [notesEnabled, setNotesEnabled] = useState(false);

  // DnD sensors — options are hoisted module-scope constants so this is
  // stable across renders (see POINTER_SENSOR_OPTIONS/KEYBOARD_SENSOR_OPTIONS)
  // instead of being a moving dependency of the `steps` useMemo below.
  const sensors = useSensors(
    useSensor(PointerSensor, POINTER_SENSOR_OPTIONS),
    useSensor(KeyboardSensor, KEYBOARD_SENSOR_OPTIONS)
  );

  const activeStage = activeId
    ? (stages.find(s => s.id === activeId) ?? null)
    : null;

  // -------------------------------------------------------------------------
  // Derived
  // -------------------------------------------------------------------------

  const generatedTypeName = useMemo(() => {
    if (!displayName.trim()) {
      return '';
    }
    const snakeCase = displayName
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_|_$/g, '');
    return `org_${snakeCase}`;
  }, [displayName]);

  const hasTimelineSuffix = useMemo(
    () => generatedTypeName.endsWith('_timeline'),
    [generatedTypeName]
  );

  const { data: existingTypeNames = new Set<string>() } = useQuery({
    queryKey: ['objectTypes', orgId, 'typeNames'],
    queryFn: async () => {
      const client = createSchemaClient(orgId);
      const types = await client.getObjectTypes();
      return new Set(types.map(t => t.entity_type_name));
    },
    enabled: !!orgId && isOpen,
    staleTime: 30_000,
  });

  const hasTypeNameCollision =
    generatedTypeName !== '' && existingTypeNames.has(generatedTypeName);

  // Dirty when any field has been touched. Step navigation (step !== 1)
  // is no longer a dirty signal — the wizard resets itself on reopen, so
  // the only meaningful "dirtiness" is user-entered data.
  const isDirty = useMemo(() => {
    return (
      displayName.trim() !== '' ||
      pluralName.trim() !== '' ||
      icon !== null ||
      ontologyKind !== null ||
      isPipelineable ||
      pipelineName.trim() !== '' ||
      properties.length > 0 ||
      associations.length > 0 ||
      notesEnabled
    );
  }, [
    displayName,
    pluralName,
    icon,
    ontologyKind,
    isPipelineable,
    pipelineName,
    properties,
    associations,
    notesEnabled,
  ]);

  const validationErrors = useMemo(() => {
    const errors: Record<string, { name?: string }> = {};
    const nameSet = new Set<string>();
    stages.forEach(stage => {
      const stageErrors: { name?: string } = {};
      if (!stage.name.trim()) {
        stageErrors.name = 'Name is required';
      } else if (nameSet.has(stage.name.toLowerCase().trim())) {
        stageErrors.name = 'Duplicate stage name';
      }
      nameSet.add(stage.name.toLowerCase().trim());
      if (stageErrors.name) {
        errors[stage.id] = stageErrors;
      }
    });
    return errors;
  }, [stages]);

  const hasValidationErrors = Object.keys(validationErrors).length > 0;

  // -------------------------------------------------------------------------
  // FormProvider config
  // -------------------------------------------------------------------------

  const fields = useMemo(
    () => ({
      displayName: {
        step: FORM_STEP_BASICS,
        validate: (v: unknown) =>
          validateDisplayName(v, { hasTimelineSuffix, hasTypeNameCollision }),
      },
      pluralName: {
        step: FORM_STEP_BASICS,
        validate: validatePluralName,
      },
      icon: {
        step: FORM_STEP_BASICS,
        validate: validateIcon,
      },
      ontologyKind: {
        step: FORM_STEP_KIND,
        validate: validateOntologyKind,
      },
      pipelineName: {
        step: FORM_STEP_PIPELINE,
        validate: (v: unknown) => validatePipelineName(v, isPipelineable),
      },
    }),
    [hasTimelineSuffix, hasTypeNameCollision, isPipelineable]
  );

  const values = useMemo(
    () => ({ displayName, pluralName, icon, ontologyKind, pipelineName }),
    [displayName, pluralName, icon, ontologyKind, pipelineName]
  );

  // -------------------------------------------------------------------------
  // Field handlers
  // -------------------------------------------------------------------------

  const capitalizeFirstLetter = (value: string) =>
    value.charAt(0).toUpperCase() + value.slice(1);

  const handleDisplayNameBlur = useCallback(() => {
    if (displayName.trim()) {
      const capitalized = capitalizeFirstLetter(displayName.trim());
      if (capitalized !== displayName) {
        setDisplayName(capitalized);
        if (pluralName === displayName + 's') {
          setPluralName(capitalized + 's');
        }
        if (isPipelineable && pipelineName === `${displayName} Pipeline`) {
          setPipelineName(`${capitalized} Pipeline`);
        }
      }
    }
  }, [displayName, pluralName, isPipelineable, pipelineName]);

  const handleDisplayNameChange = useCallback(
    (value: string) => {
      setDisplayName(value);
      if (!pluralName || pluralName === displayName + 's') {
        setPluralName(value ? value + 's' : '');
      }
      if (
        isPipelineable &&
        (!pipelineName || pipelineName === `${displayName} Pipeline`)
      ) {
        setPipelineName(value ? `${value} Pipeline` : '');
      }
    },
    [displayName, pluralName, isPipelineable, pipelineName]
  );

  // -------------------------------------------------------------------------
  // Stage handlers
  // -------------------------------------------------------------------------

  const handleStageNameChange = useCallback((id: string, name: string) => {
    setStages(prev => prev.map(s => (s.id === id ? { ...s, name } : s)));
  }, []);

  const handleStageDelete = useCallback((id: string) => {
    setStages(prev => {
      const filtered = prev.filter(s => s.id !== id);
      return filtered.map((s, i) => ({ ...s, order: i + 1 }));
    });
  }, []);

  const handleStageAdd = useCallback(() => {
    setStages(prev => {
      const newStage: StageItem = {
        id: crypto.randomUUID(),
        name: '',
        order: prev.length + 1,
      };
      return [...prev, newStage];
    });
  }, []);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  }, []);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }
    setStages(prev => {
      const oldIndex = prev.findIndex(s => s.id === active.id);
      const newIndex = prev.findIndex(s => s.id === over.id);
      const reordered = arrayMove(prev, oldIndex, newIndex);
      return reordered.map((s, i) => ({ ...s, order: i + 1 }));
    });
  }, []);

  const handleDragCancel = useCallback(() => {
    setActiveId(null);
  }, []);

  /**
   * Picking the kind is what turns the pipeline on or off — there is no
   * separate toggle. Choosing the pipeline kind seeds default stages and a
   * pipeline name so the Pipeline step opens ready to edit; choosing any other
   * kind drops back to no pipeline. Seeded stages are deliberately NOT cleared
   * on switching away, so a customer who changes their mind twice doesn't lose
   * stage names they already typed.
   */
  const handleOntologyKindChange = useCallback(
    (value: OntologyKind) => {
      setOntologyKind(value);
      const ridesPipeline = kindRidesPipeline(value);
      setIsPipelineable(ridesPipeline);
      if (ridesPipeline) {
        if (stages.length === 0) {
          setStages(createDefaultStages());
        }
        if (!pipelineName && displayName.trim()) {
          setPipelineName(`${displayName.trim()} Pipeline`);
        }
      }
    },
    [stages.length, pipelineName, displayName]
  );

  // -------------------------------------------------------------------------
  // Submit
  // -------------------------------------------------------------------------

  const resetForm = useCallback(() => {
    setDisplayName('');
    setPluralName('');
    setIcon(null);
    setOntologyKind(null);
    setIsPipelineable(false);
    setPipelineName('');
    setStages([]);
    setProperties([]);
    setAssociations([]);
    setNotesEnabled(false);
    setError(null);
  }, []);

  const handleSuccess = useCallback(async () => {
    await invalidateSchemaCaches(queryClient, orgId);
    await Promise.allSettled([
      properties.length > 0
        ? queryClient.invalidateQueries({
            queryKey: ['propertyTypes', orgId],
            exact: false,
          })
        : Promise.resolve(),
      isPipelineable
        ? queryClient.invalidateQueries({
            queryKey: ['pipelines', orgId],
            exact: false,
          })
        : Promise.resolve(),
      refreshCurrentOrg(),
    ]);
    onCreated(generatedTypeName);
    onClose();
    resetForm();
  }, [
    queryClient,
    orgId,
    properties.length,
    isPipelineable,
    generatedTypeName,
    onCreated,
    onClose,
    resetForm,
    refreshCurrentOrg,
  ]);

  const doSubmit = useCallback(async () => {
    setError(null);
    if (!user?.id) {
      setError('User ID not available');
      throw new Error('User ID not available');
    }
    // The Type step gates advancing past it, so the wizard cannot reach submit
    // without a kind. Guard anyway rather than defaulting: stamping a guessed
    // kind is exactly the anonymous-type bug this step exists to end.
    if (!ontologyKind) {
      setError('Pick what kind of thing this is before creating it.');
      throw new Error('Ontology kind not selected');
    }
    try {
      const eventSteps = buildObjectTypeEvents(String(user.id), {
        entityTypeName: generatedTypeName,
        displayName,
        pluralName,
        icon: icon || '',
        ontologyKind,
        isPipelineable,
        pipelineName,
        stages,
        properties,
        associations,
        notesEnabled,
      });
      const allEvents = eventSteps.flatMap(s => s.events);
      const client = createEventsClient(orgId);
      await client.submitEvents(allEvents);
      await handleSuccess();
    } catch (err) {
      const msg = getUserFriendlyEventError(err);
      setError(`${msg} (You can retry)`);
      throw err;
    }
  }, [
    user?.id,
    orgId,
    generatedTypeName,
    displayName,
    pluralName,
    icon,
    ontologyKind,
    isPipelineable,
    pipelineName,
    stages,
    properties,
    associations,
    notesEnabled,
    handleSuccess,
  ]);

  // Reset form state when the wizard is closed (the Wizard handles its
  // own dirty-close confirmation; this just clears local state after the
  // close commits).
  const handleClose = useCallback(() => {
    onClose();
    // Defer the reset to the next tick so the closing animation doesn't
    // snap to an empty form mid-exit.
    setTimeout(() => {
      resetForm();
    }, 0);
  }, [onClose, resetForm]);

  return (
    <FormProvider fields={fields} values={values} fieldLabels={FIELD_LABELS}>
      <CreateObjectTypeWizardInner
        isOpen={isOpen}
        onClose={handleClose}
        onCreated={onCreated}
        orgId={orgId}
        displayName={displayName}
        pluralName={pluralName}
        icon={icon}
        ontologyKind={ontologyKind}
        isPipelineable={isPipelineable}
        pipelineName={pipelineName}
        stages={stages}
        properties={properties}
        associations={associations}
        notesEnabled={notesEnabled}
        error={error}
        activeStage={activeStage}
        validationErrors={validationErrors}
        hasValidationErrors={hasValidationErrors}
        generatedTypeName={generatedTypeName}
        sensors={sensors}
        isDirty={isDirty}
        onDisplayNameChange={handleDisplayNameChange}
        onDisplayNameBlur={handleDisplayNameBlur}
        onPluralNameChange={setPluralName}
        onIconChange={setIcon}
        onOntologyKindChange={handleOntologyKindChange}
        onPipelineNameChange={setPipelineName}
        onStageNameChange={handleStageNameChange}
        onStageDelete={handleStageDelete}
        onStageAdd={handleStageAdd}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
        onPropertiesChange={setProperties}
        onAssociationsChange={setAssociations}
        onNotesEnabledChange={setNotesEnabled}
        setError={setError}
        doSubmit={doSubmit}
      />
    </FormProvider>
  );
}
